import instance from '.'
import { err, getResult } from '../utils/functions'
import { APIResult } from '../utils/types'
import { List, ListItem } from './types/lists'

const RANDOM_GUEST_SESSION_STORAGE_KEY = 'random_guest_session_id'

const getRandomGuestSessionId = (): string | undefined => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return undefined
  const existing = localStorage.getItem(RANDOM_GUEST_SESSION_STORAGE_KEY)
  if (existing && /^[a-zA-Z0-9_-]{8,128}$/.test(existing)) {
    return existing
  }
  const created = `g_${Math.random().toString(36).slice(2, 12)}_${Date.now().toString(36)}`
  localStorage.setItem(RANDOM_GUEST_SESSION_STORAGE_KEY, created)
  return created
}

export type DailyCompletionRankResult = {
  dateKey: string
  listId: number
  correct: number
  total: number
  guesses: number
  completionMs: number
  rank: number
  totalParticipants: number
  percentile: number
  topPercentLabel: string
  accepted?: boolean
  cheatReason?: string
  soloXpAwarded?: number
  soloStats?: {
    userId: number
    soloXp: number
    soloLevel: number
    dailyStreak: number
    dailyBestStreak: number
    dailyLastAttemptDate?: string | null
  }
}

export type DailyLeaderboardRow = {
  userId: number
  username: string
  image?: string
  correct: number
  total: number
  guesses: number
  completionMs: number
  score: number
  rank: number
}

export type MyDailyCompletion = {
  dateKey: string
  listId: number
  correct: number
  total: number
  guesses: number
  completionMs: number
  rank?: number
  totalParticipants: number
  percentile?: number
  topPercentLabel?: string
  accepted?: boolean
  cheatReason?: string
  createdAt?: string
  updatedAt?: string
}

export function createList(params: {
  prompt?: string
  items?: ListItem[]
  list?: List
}): Promise<APIResult<List>> {
  return instance
    .post('/api/list/create', params)
    .catch((e) => err(e))
    .then((res) => getResult<List>(res))
}

export function deleteList(listId: number): Promise<APIResult<number>> {
  return instance
    .delete(`/api/list/${listId}`)
    .catch((e) => err(e))
    .then((res) => getResult<number>(res))
}

export function saveList(
  listId: number,
  prompt: string,
  items?: ListItem[],
  categories?: string[],
  tags?: string[],
  isPrivate?: boolean
): Promise<APIResult<number>> {
  return instance
    .post(`/api/list/${listId}`, {
      prompt,
      items,
      categories,
      tags,
      private: isPrivate,
    })
    .catch((e) => err(e))
    .then((res) => getResult<number>(res))
}

export function rateList(listId: number, rating: number): Promise<APIResult<void>> {
  return instance
    .post('/api/list/rate', {
      listId,
      rating,
    })
    .catch((e) => err(e))
    .then((res) => getResult<void>(res))
}

export function playedList(listId: number): Promise<APIResult<void>> {
  return instance
    .post('/api/list/played', {
      listId,
    })
    .catch((e) => err(e))
    .then((res) => getResult<void>(res))
}

export function reportList(params: {
  listId: number
  reason?: string
  details?: string
}): Promise<APIResult<void>> {
  return instance
    .post('/api/list/report', params)
    .catch((e) => err(e))
    .then((res) => getResult<void>(res))
}

export function getLists(params?: {
  page?: number
  limit?: number
  search?: string
  tags?: string[]
  categories?: string[]
  sort?: string
}): Promise<APIResult<{ lists: List[]; total: number }>> {
  return instance
    .get('/api/list', {
      params: params,
    })
    .catch((e) => err(e))
    .then((res) => getResult<{ lists: List[]; total: number }>(res))
}

export function getListTags(): Promise<APIResult<string[]>> {
  return instance
    .get('/api/list/tags')
    .catch((e) => err(e))
    .then((res) => getResult<string[]>(res))
}
export function getListCategories(): Promise<APIResult<string[]>> {
  return instance
    .get('/api/list/categories')
    .catch((e) => err(e))
    .then((res) => getResult<string[]>(res))
}

export function getMyLists(params?: {
  page?: number
  limit?: number
  search?: string
  tags?: string[]
  categories?: string[]
  sort?: string
}): Promise<APIResult<{ lists: List[]; total: number }>> {
  return instance
    .get('/api/list/myLists', {
      params: params,
    })
    .catch((e) => err(e))
    .then((res) => getResult<{ lists: List[]; total: number }>(res))
}

export async function getRandomList(seedList?: number): Promise<APIResult<List>> {
  const guestSessionId = getRandomGuestSessionId()
  return instance
    .get('/api/list/random', {
      params: {
        list: seedList,
        ...(guestSessionId ? { guestSessionId } : {}),
      },
    })
    .catch((e) => err(e))
    .then((res) => getResult<List>(res))
}

export function getDailyList(): Promise<APIResult<List>> {
  return instance
    .get('/api/list/daily')
    .catch((e) => err(e))
    .then((res) => getResult<List>(res))
}

export function submitDailyCompletion(params: {
  listId: number
  correct: number
  total: number
  guesses: number
  completionMs: number
}): Promise<APIResult<DailyCompletionRankResult>> {
  return instance
    .post('/api/list/daily/complete', params)
    .catch((e) => err(e))
    .then((res) => getResult<DailyCompletionRankResult>(res))
}

export function getDailyLeaderboard(params?: {
  date?: string
  limit?: number
  offset?: number
}): Promise<APIResult<DailyLeaderboardRow[]>> {
  return instance
    .get('/api/list/daily/leaderboard', { params })
    .catch((e) => err(e))
    .then((res) => getResult<DailyLeaderboardRow[]>(res))
}

export function getMyDailyCompletion(params?: { date?: string }): Promise<APIResult<MyDailyCompletion>> {
  return instance
    .get('/api/list/daily/me', { params })
    .catch((e) => err(e))
    .then((res) => getResult<MyDailyCompletion>(res))
}

export function getList(id: number): Promise<APIResult<List>> {
  return instance
    .get(`/api/list/${id}`)
    .catch((e) => err(e))
    .then((res) => getResult<List>(res))
}
